// Pure aggregations for the chapter Statistics tab.
//
// These functions take already-loaded pitch, review, and chapter arrays and
// derive the metrics the tab renders. Keeping them pure and free of React /
// Firestore means they can be reasoned about in isolation and recomposed for
// exports (year-in-review card, printable report) without re-fetching.
//
// Min-sample gating: percentages backed by fewer than MIN_SAMPLE_FOR_PCT
// observations return null, and the caller renders an em-dash. The threshold
// is deliberately visible so future tuning (e.g. "show at 3 for small chapters")
// happens in one place.

import { resolveAwardAmount } from "../../../helpers/awardAmount";
import { isManualAward } from "../../../helpers/manualAward";
import { isLMIZip } from "../../../helpers/lmiZips";
import { PITCH_CATEGORIES } from "../../../data/pitchCategories";

export const MIN_SAMPLE_FOR_PCT = 5;

// Self-identification buckets — the superset the application form exposes.
// Rendered in this order so the breakdown chart is stable across chapters
// and time periods. Pitches may carry multiple tags; one founder can be e.g.
// both "Women Owned/Led" and "BIPOC Owned/Led" so category counts sum to
// MORE than the total applicant pool.
export const SELF_ID_CATEGORIES = [
  "Women Owned/Led",
  "BIPOC Owned/Led",
  "Minority Owned/Led",
  "LGBTQ+ Owned/Led",
  "Veteran Owned/Led",
  "Disabled Owned/Led",
  "Student Owned/Led",
];

// ── Date / quarter helpers ─────────────────────────────────────────────
// Firestore returns Timestamp objects in some paths, ISO strings in others,
// and plain Dates after .toDate() calls — this accepts all three.
export function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") {
    try { return value.toDate(); } catch (_) { return null; }
  }
  if (value instanceof Date) return isNaN(value) ? null : value;
  const parsed = new Date(value);
  return isNaN(parsed) ? null : parsed;
}

export function quarterKey(date) {
  if (!date) return null;
  const q = Math.floor(date.getMonth() / 3) + 1;
  return `Q${q} ${date.getFullYear()}`;
}

export function monthKey(date) {
  if (!date) return null;
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${date.getFullYear()}-${m}`;
}

export function yearKey(date) {
  if (!date) return null;
  return String(date.getFullYear());
}

// Chronological compare for "Q1 2024"-style keys.
export function compareQuarterKey(a, b) {
  const pa = a.match(/Q(\d) (\d{4})/);
  const pb = b.match(/Q(\d) (\d{4})/);
  if (!pa || !pb) return a.localeCompare(b);
  const ya = Number(pa[2]); const yb = Number(pb[2]);
  if (ya !== yb) return ya - yb;
  return Number(pa[1]) - Number(pb[1]);
}

// ── Percentages with min-sample gating ────────────────────────────────
export function pctOrNull(numerator, denominator, minSample = MIN_SAMPLE_FOR_PCT) {
  if (!Number.isFinite(denominator) || denominator < minSample) return null;
  if (!Number.isFinite(numerator) || numerator < 0) return null;
  return Math.round((numerator / denominator) * 100);
}

// ── KPI summary ────────────────────────────────────────────────────────
// Takes the FULL pitch set, manually logged grant awards included. Grants
// funded and dollars deployed count every winner (that's the chapter's real
// impact); applications and win rate count only real applications, so a
// logged pitch-competition award can't inflate the pipeline's conversion.
export function summarizeKPIs(pitches, chaptersByName) {
  const applications = pitches.filter((p) => !isManualAward(p));
  const winners = pitches.filter((p) => p.isWinner);
  const applicationWinnerCount = applications.filter((p) => p.isWinner).length;
  const dollars = winners.reduce((sum, p) => sum + resolveAwardAmount(p, chaptersByName), 0);
  return {
    totalApplications: applications.length,
    winnerCount: winners.length,
    applicationWinnerCount,
    dollarsDeployed: dollars,
    winRatePct: pctOrNull(applicationWinnerCount, applications.length), // null when applications < MIN_SAMPLE_FOR_PCT
  };
}

// ── Applications over time (grouped by month or quarter) ──────────────
export function applicationsOverTime(pitches, granularity = "quarter") {
  const keyFn = granularity === "month" ? monthKey : quarterKey;
  const buckets = new Map();
  for (const p of pitches) {
    const key = keyFn(toDate(p.createdAt));
    if (!key) continue;
    if (!buckets.has(key)) buckets.set(key, { key, total: 0, winners: 0 });
    const b = buckets.get(key);
    b.total += 1;
    if (p.isWinner) b.winners += 1;
  }
  const list = Array.from(buckets.values());
  list.sort((a, b) => granularity === "quarter"
    ? compareQuarterKey(a.key, b.key)
    : a.key.localeCompare(b.key));
  return list;
}

// ── Self-identification breakdown ──────────────────────────────────────
// Returns rows in SELF_ID_CATEGORIES order with counts + dollar totals for
// applicants and winners. `winPct` uses min-N gating against applicants in
// the category; `sharePct` is that category's share of the applicant pool
// (no gating — share of a sample is meaningful even at small n, it's the
// percentage-of-a-percentage inference that becomes noisy).
//
// Founders may tag multiple categories; rows do not sum to the pool total.
export function selfIdBreakdown(pitches, chaptersByName) {
  const totalPitches = pitches.length;
  const totalWinners = pitches.filter((p) => p.isWinner).length;
  const notSpecified = {
    category: "Not specified",
    applicants: 0,
    winners: 0,
    dollars: 0,
    applicantShare: null,
    winShare: null,
    winPct: null,
  };
  const rows = SELF_ID_CATEGORIES.map((cat) => {
    let applicants = 0;
    let winners = 0;
    let dollars = 0;
    for (const p of pitches) {
      const tags = Array.isArray(p.selfIdentification) ? p.selfIdentification : [];
      if (!tags.includes(cat)) continue;
      applicants += 1;
      if (p.isWinner) {
        winners += 1;
        dollars += resolveAwardAmount(p, chaptersByName);
      }
    }
    return {
      category: cat,
      applicants,
      winners,
      dollars,
      applicantShare: totalPitches > 0 ? Math.round((applicants / totalPitches) * 100) : 0,
      winShare: totalWinners > 0 ? Math.round((winners / totalWinners) * 100) : 0,
      winPct: pctOrNull(winners, applicants),
    };
  });

  // Applicants who tagged nothing. Useful to understand how complete the
  // self-ID data is before reading too much into the percentages above.
  for (const p of pitches) {
    const tags = Array.isArray(p.selfIdentification) ? p.selfIdentification : [];
    if (tags.length === 0) {
      notSpecified.applicants += 1;
      if (p.isWinner) {
        notSpecified.winners += 1;
        notSpecified.dollars += resolveAwardAmount(p, chaptersByName);
      }
    }
  }
  notSpecified.applicantShare = totalPitches > 0
    ? Math.round((notSpecified.applicants / totalPitches) * 100)
    : 0;
  notSpecified.winShare = totalWinners > 0
    ? Math.round((notSpecified.winners / totalWinners) * 100)
    : 0;
  notSpecified.winPct = pctOrNull(notSpecified.winners, notSpecified.applicants);

  return { rows, notSpecified, totalPitches, totalWinners };
}

// ── Referral / acquisition channels (heardAbout) ──────────────────────
// `heardAbout` is a free-text field, so the raw values are messy: case
// variants ("INSTAGRAM" vs "instagram"), typos ("Instaram"), punctuation
// ("Instagram!"), and many ways to say the same thing ("friend", "a friend",
// "friend of mine"). We normalize at display time only — Firestore docs
// are left untouched. Two-pass normalization:
//   1. Pattern match against a known-channel list → canonical label.
//   2. Fallback: lowercased + first-letter-capitalized of the trimmed input,
//      so unrecognized sources still merge across casing variants.
// To add a new canonical bucket, add an entry to REFERRAL_CHANNEL_PATTERNS;
// matching is first-hit-wins, so order more specific patterns before broader
// ones. The match key strips punctuation/whitespace, so patterns work against
// the squashed form (e.g. "linkedin" matches "Linked-In" and "linked in").
const REFERRAL_CHANNEL_PATTERNS = [
  { canonical: "Instagram", patterns: [/^insta/, /^ig$/, /instgram/, /instagran/, /instaram/] },
  { canonical: "LinkedIn",  patterns: [/^linkedin$/, /linkdin/, /linkedln/] },
  { canonical: "Facebook",  patterns: [/^facebook/, /^fb$/, /^meta$/] },
  { canonical: "TikTok",    patterns: [/^tiktok/] },
  { canonical: "Twitter / X", patterns: [/^twitter/, /^x$/, /^xcom$/, /^twitterx$/] },
  { canonical: "YouTube",   patterns: [/^youtube/, /^yt$/] },
  { canonical: "Reddit",    patterns: [/^reddit/] },
  { canonical: "Threads",   patterns: [/^threads$/] },
  { canonical: "Slack / Discord", patterns: [/^slack/, /discord/] },
  { canonical: "Google search",   patterns: [/google/, /^searchengine/, /^websearch/, /^search$/] },
  { canonical: "News or press",   patterns: [/^news/, /^press/, /article/, /journalist/, /newspaper/, /paper$/, /podcast/, /radio/] },
  { canonical: "Email or newsletter", patterns: [/^email/, /newsletter/, /substack/, /mailing/, /listserv/] },
  { canonical: "Event",     patterns: [/event/, /networking/, /meetup/, /conference/, /^pitch/, /panel/] },
  { canonical: "Partner or sponsor", patterns: [/partner/, /sponsor/, /chamber/] },
  { canonical: "Friend or family",   patterns: [/friend/, /family/, /relative/, /wordofmouth/, /^wom$/, /someoneiknow/, /colleague/, /coworker/, /neighbor/] },
  { canonical: "GNF website",        patterns: [/^goodneighborfund/, /^gnf/, /^website$/, /^thewebsite$/] },
];

// Strip everything except a-z0-9 for matching. "Linked-In!" → "linkedin",
// "Word of Mouth" → "wordofmouth", "Instagram :)" → "instagram".
function squashForMatch(s) {
  return s.toLowerCase().replace(/[^a-z0-9]/g, "");
}

// Capitalize first letter of the trimmed lowercased input, leave the rest
// alone. Used as the display label for unrecognized sources so case variants
// merge ("RANDOM source" + "random source" → "Random source") without
// mangling things like "ChatGPT" into "Chatgpt".
function fallbackLabel(trimmed) {
  if (!trimmed) return trimmed;
  const lower = trimmed.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function normalizeReferralSource(raw) {
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  if (!trimmed) return "Not specified";
  const key = squashForMatch(trimmed);
  if (!key) return "Not specified";
  for (const channel of REFERRAL_CHANNEL_PATTERNS) {
    for (const pattern of channel.patterns) {
      if (pattern.test(key)) return channel.canonical;
    }
  }
  return fallbackLabel(trimmed);
}

export function referralBreakdown(pitches) {
  const map = new Map();
  for (const p of pitches) {
    const key = normalizeReferralSource(p.heardAbout);
    if (!map.has(key)) map.set(key, { source: key, applicants: 0, winners: 0 });
    const row = map.get(key);
    row.applicants += 1;
    if (p.isWinner) row.winners += 1;
  }
  const rows = Array.from(map.values())
    .map((r) => ({ ...r, winPct: pctOrNull(r.winners, r.applicants) }))
    .sort((a, b) => b.applicants - a.applicants);
  return rows;
}

// ── Geographic reach ──────────────────────────────────────────────────
// Groups pitches by zipCode. Only accepts real 5-digit US zips — imported
// pitch docs sometimes carry "nan" / "N/A" / blank values, and those would
// otherwise show up as a fake top row in the ranked list. Invalid zips are
// silently excluded; the count of skipped pitches is reported separately by
// the map component so the director knows the coverage gap exists.
export function zipDistribution(pitches) {
  const map = new Map();
  for (const p of pitches) {
    const zip = normalizeUSZip(p.zipCode);
    if (!zip) continue;
    if (!map.has(zip)) map.set(zip, { zip, applicants: 0, winners: 0 });
    const row = map.get(zip);
    row.applicants += 1;
    if (p.isWinner) row.winners += 1;
  }
  return Array.from(map.values()).sort((a, b) => b.applicants - a.applicants);
}

// Share of applicants from low-to-moderate-income ZIPs. Denominator is the
// pitches we *can* classify (those carrying a real 5-digit US zip), so the
// percentage isn't dragged down by missing-zip rows. The caller renders the
// raw counts alongside so a reviewer can see the coverage gap.
export function lmiShare(pitches) {
  let applicantsWithZip = 0;
  let lmiApplicants = 0;
  let lmiWinners = 0;
  let winnersWithZip = 0;
  for (const p of pitches) {
    const zip = normalizeUSZip(p.zipCode);
    if (!zip) continue;
    applicantsWithZip += 1;
    const lmi = isLMIZip(zip);
    if (lmi) lmiApplicants += 1;
    if (p.isWinner) {
      winnersWithZip += 1;
      if (lmi) lmiWinners += 1;
    }
  }
  return {
    applicantsWithZip,
    lmiApplicants,
    winnersWithZip,
    lmiWinners,
    applicantSharePct: applicantsWithZip > 0
      ? Math.round((lmiApplicants / applicantsWithZip) * 100)
      : null,
    winnerSharePct: winnersWithZip > 0
      ? Math.round((lmiWinners / winnersWithZip) * 100)
      : null,
  };
}

// Exported so GeographicReach can share a single definition of "what counts
// as a zip" with the aggregator.
export function normalizeUSZip(value) {
  if (value == null) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  const match = raw.match(/^(\d{5})(?:-\d{4})?$/);
  return match ? match[1] : "";
}

// ── Pipeline health ───────────────────────────────────────────────────
// Repeat applicants: emails (case-insensitive) that appear on >1 pitch.
export function repeatApplicants(pitches) {
  const byEmail = new Map();
  for (const p of pitches) {
    const email = typeof p.email === "string" ? p.email.trim().toLowerCase() : "";
    if (!email) continue;
    byEmail.set(email, (byEmail.get(email) || 0) + 1);
  }
  let repeaters = 0;
  let extraSubmissions = 0;
  for (const count of byEmail.values()) {
    if (count > 1) {
      repeaters += 1;
      extraSubmissions += count - 1;
    }
  }
  return { uniqueEmails: byEmail.size, repeaters, extraSubmissions };
}

// Days from pitch creation to awardedAt, averaged over winners where both
// timestamps are present. Historical winners that predate awardedAt are
// filtered out rather than zero-filled so the average isn't dragged down.
export function daysToDecision(pitches) {
  const winners = pitches.filter((p) => p.isWinner);
  const samples = [];
  for (const p of winners) {
    const created = toDate(p.createdAt);
    const awarded = toDate(p.awardedAt);
    if (!created || !awarded) continue;
    const diffMs = awarded.getTime() - created.getTime();
    if (diffMs < 0) continue;
    samples.push(diffMs / (1000 * 60 * 60 * 24));
  }
  if (samples.length === 0) {
    return { avgDays: null, medianDays: null, sampleSize: 0, winnerCount: winners.length };
  }
  samples.sort((a, b) => a - b);
  const sum = samples.reduce((s, v) => s + v, 0);
  const avg = sum / samples.length;
  const mid = Math.floor(samples.length / 2);
  const median = samples.length % 2 === 0
    ? (samples[mid - 1] + samples[mid]) / 2
    : samples[mid];
  return {
    avgDays: Math.round(avg * 10) / 10,
    medianDays: Math.round(median * 10) / 10,
    sampleSize: samples.length,
    winnerCount: winners.length,
  };
}

// Quarter-over-quarter applicant growth, expressed as (current - previous) /
// previous. Returns null when either quarter has < MIN_SAMPLE_FOR_PCT pitches
// (percentage swings on tiny samples are noise). `previous` and `current` are
// the newest two quarters present in the data.
export function quarterOverQuarterGrowth(pitches) {
  const series = applicationsOverTime(pitches, "quarter");
  if (series.length < 2) return { previous: null, current: null, growthPct: null };
  const current = series[series.length - 1];
  const previous = series[series.length - 2];
  if (previous.total < MIN_SAMPLE_FOR_PCT || current.total < MIN_SAMPLE_FOR_PCT) {
    return { previous, current, growthPct: null };
  }
  const growth = ((current.total - previous.total) / previous.total) * 100;
  return { previous, current, growthPct: Math.round(growth) };
}

// ── Reviewer engagement ───────────────────────────────────────────────
// Operates on raw review docs. `users` is passed so we can tie reviewerId
// back to a chapter for scoped views even when a review was written before
// a chapter was assigned.
export function reviewerEngagement(reviews, users, chapterFilter) {
  const filteredReviews = chapterFilter
    ? reviews.filter((r) => r.chapter === chapterFilter)
    : reviews;
  const reviewerIds = new Set();
  const ratingCounts = { Favorite: 0, Consideration: 0, Pass: 0, Ineligible: 0 };
  const pitchReviewCounts = new Map(); // pitchId → review count

  for (const r of filteredReviews) {
    if (r.reviewerId) reviewerIds.add(r.reviewerId);
    if (r.overallLpRating && ratingCounts[r.overallLpRating] !== undefined) {
      ratingCounts[r.overallLpRating] += 1;
    }
    if (r.pitchId) {
      pitchReviewCounts.set(r.pitchId, (pitchReviewCounts.get(r.pitchId) || 0) + 1);
    }
  }

  const counts = Array.from(pitchReviewCounts.values());
  const avgReviewsPerPitch = counts.length > 0
    ? Math.round((counts.reduce((s, c) => s + c, 0) / counts.length) * 10) / 10
    : 0;

  // LP pool size for this chapter. Used to report "how many of our LPs
  // participated" (not just "how many distinct reviewers", which double-counts
  // people who changed chapters).
  const totalLPsInScope = users.filter((u) => {
    const isPortalRole = u.role === "lp" || u.role === "chapter_director" || u.role === "superAdmin";
    if (!isPortalRole) return false;
    if (!chapterFilter) return true;
    return u.chapter === chapterFilter;
  }).length;

  return {
    activeReviewerCount: reviewerIds.size,
    totalLPsInScope,
    ratingDistribution: ratingCounts,
    avgReviewsPerPitch,
    pitchesReviewed: counts.length,
    totalReviews: filteredReviews.length,
  };
}

// ── Category breakdown ────────────────────────────────────────────────
// Applicants, winners, dollars deployed, and avg award size per AI-assigned
// category. Each pitch is in exactly one bucket, so rows sum to the applicant
// total (plus the "Uncategorized" overflow row for pitches that haven't been
// classified yet).
export function categoryBreakdown(pitches, chaptersByName) {
  const totalPitches = pitches.length;
  const totalWinners = pitches.filter((p) => p.isWinner).length;

  const map = new Map();
  for (const cat of PITCH_CATEGORIES) {
    map.set(cat, { category: cat, applicants: 0, winners: 0, dollars: 0 });
  }
  const uncategorized = { category: "Uncategorized", applicants: 0, winners: 0, dollars: 0 };

  for (const p of pitches) {
    const raw = typeof p.category === "string" ? p.category : "";
    const bucket = map.has(raw) ? map.get(raw) : uncategorized;
    bucket.applicants += 1;
    if (p.isWinner) {
      bucket.winners += 1;
      bucket.dollars += resolveAwardAmount(p, chaptersByName);
    }
  }

  const decorate = (row) => ({
    ...row,
    applicantShare: totalPitches > 0
      ? Math.round((row.applicants / totalPitches) * 100)
      : 0,
    winShare: totalWinners > 0
      ? Math.round((row.winners / totalWinners) * 100)
      : 0,
    winPct: pctOrNull(row.winners, row.applicants),
    avgAward: row.winners > 0 ? Math.round(row.dollars / row.winners) : 0,
  });

  const rows = PITCH_CATEGORIES.map((cat) => decorate(map.get(cat)));
  return {
    rows,
    uncategorized: decorate(uncategorized),
    totalPitches,
    totalWinners,
  };
}

// ── Categories over time ──────────────────────────────────────────────
// Per-category time series, sharing a single x-axis built from the union of
// all bucket keys present in the data. Returned `keys` is chronologically
// sorted; each `series[cat]` is a same-length array (zero-filled where the
// category had no submissions in that bucket) so callers can render
// small-multiples sparklines without per-panel alignment work.
//
// Pitches missing a recognized category are dropped — they show up in the
// "Uncategorized" row of `categoryBreakdown` instead.
export function categoriesOverTime(pitches, granularity = "quarter") {
  const keyFn = granularity === "month" ? monthKey : quarterKey;
  const keySet = new Set();
  for (const p of pitches) {
    const k = keyFn(toDate(p.createdAt));
    if (k) keySet.add(k);
  }
  const keys = Array.from(keySet).sort(
    granularity === "quarter"
      ? compareQuarterKey
      : (a, b) => a.localeCompare(b)
  );
  const indexByKey = new Map(keys.map((k, i) => [k, i]));

  const series = {};
  for (const cat of PITCH_CATEGORIES) {
    series[cat] = keys.map((k) => ({ key: k, total: 0, winners: 0 }));
  }

  for (const p of pitches) {
    const cat = typeof p.category === "string" ? p.category : "";
    if (!PITCH_CATEGORIES.includes(cat)) continue;
    const k = keyFn(toDate(p.createdAt));
    if (!k) continue;
    const i = indexByKey.get(k);
    if (i == null) continue;
    series[cat][i].total += 1;
    if (p.isWinner) series[cat][i].winners += 1;
  }

  return { keys, series };
}

// ── Category × Self-ID cross ──────────────────────────────────────────
// For each AI category, the count of applicants carrying each self-ID tag
// (and how many tagged nothing). Founders can carry multiple self-ID tags,
// so the row's tag counts can sum to more than the row total — same caveat
// as `selfIdBreakdown`, applied per-category.
//
// Returns rows in PITCH_CATEGORIES order; `selfIds` echoes SELF_ID_CATEGORIES
// so the caller can iterate columns without re-importing the constant.
export function categoryBySelfId(pitches) {
  const rows = PITCH_CATEGORIES.map((cat) => {
    const row = { category: cat, total: 0, notSpecified: 0, tags: {} };
    for (const sid of SELF_ID_CATEGORIES) row.tags[sid] = 0;
    return row;
  });
  const byCategory = new Map(rows.map((r) => [r.category, r]));

  for (const p of pitches) {
    const cat = typeof p.category === "string" ? p.category : "";
    const row = byCategory.get(cat);
    if (!row) continue;
    row.total += 1;
    const tags = Array.isArray(p.selfIdentification) ? p.selfIdentification : [];
    if (tags.length === 0) {
      row.notSpecified += 1;
      continue;
    }
    for (const sid of SELF_ID_CATEGORIES) {
      if (tags.includes(sid)) row.tags[sid] += 1;
    }
  }

  return { rows, selfIds: SELF_ID_CATEGORIES };
}

// ── Category × Chapter heatmap ────────────────────────────────────────
// Counts pitches by chapter (rows) × category (columns). Only meaningful
// when the loaded pitch set spans multiple chapters — the StatisticsTab
// gates this for super-admins viewing "All chapters". Returned rows are
// sorted by total applicants desc so the busiest chapter sits at the top.
export function categoryByChapter(pitches) {
  const map = new Map();
  for (const p of pitches) {
    const cat = typeof p.category === "string" ? p.category : "";
    if (!PITCH_CATEGORIES.includes(cat)) continue;
    const chapter =
      typeof p.chapter === "string" && p.chapter.trim()
        ? p.chapter.trim()
        : "Unassigned";
    if (!map.has(chapter)) {
      const init = { chapter, total: 0, byCategory: {} };
      for (const c of PITCH_CATEGORIES) init.byCategory[c] = 0;
      map.set(chapter, init);
    }
    const row = map.get(chapter);
    row.total += 1;
    row.byCategory[cat] += 1;
  }
  return Array.from(map.values()).sort((a, b) => b.total - a.total);
}
